"use client";

import type { AppRouter, BffRouterInputs, BffRouterOutputs } from "@chaski/bff";
import { auth } from "@portal/lib/firebase/client";
import * as Sentry from "@sentry/nextjs";
import { httpBatchLink, httpLink, splitLink } from "@trpc/client";
import type { Operation } from "@trpc/client";
import { createTRPCReact } from "@trpc/react-query";
import type { inferReactQueryProcedureOptions } from "@trpc/react-query";

type ReactQueryOptions = inferReactQueryProcedureOptions<AppRouter>;
export type { BffRouterOutputs, BffRouterInputs, ReactQueryOptions };

/**
 * For use exclusively in client components
 *
 * @example
 * import { trpc } from '@portal/lib/trpc/client';
 *
 * const { data } = await trpc.products.search.useQuery({
 *  pageNumber: 1,
 *  pageSize: 10,
 * });
 *
 */
export const trpc = createTRPCReact<AppRouter>();

async function getIdToken(): Promise<string | undefined> {
  try {
    const existingUser = auth.currentUser;
    if (existingUser) {
      return existingUser.getIdToken();
    }
    await auth.authStateReady();

    const user = auth.currentUser;
    if (user) {
      const token = await user.getIdToken();
      return token;
    }
  } catch (e) {
    Sentry.captureException("Error fetching id token from firebase", {
      extra: { error: e },
    });
    return undefined;
  }
}

export function createClientTrpcClient({
  getHeaders,
}: {
  getHeaders: () => Record<string, string>;
}) {
  return trpc.createClient({
    links: [
      splitLink({
        condition: shouldOptOutOfBatching,
        true: httpLink({
          url: `${process.env.NEXT_PUBLIC_BFF_BASE_URL}`,
          headers: async () => {
            const dynamic = getHeaders();
            const token = await getIdToken();
            return {
              ...dynamic,
              authorization: token ? `Bearer ${token}` : undefined,
            };
          },
        }),
        false: httpBatchLink({
          url: `${process.env.NEXT_PUBLIC_BFF_BASE_URL}`,
          headers: async () => {
            const dynamic = getHeaders();
            const token = await getIdToken();
            return {
              ...dynamic,
              authorization: token ? `Bearer ${token}` : undefined,
            };
          },
        }),
      }),
    ],
  });
}

function shouldOptOutOfBatching(op: Operation) {
  return Boolean(op.context.skipBatching);
}
